"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCartStore } from "@/lib/store/cart-store";
import { loadAnonymousCart, clearAnonymousCart } from "@/lib/utils/cart-storage";
import * as cartService from "@/services/cart";
import { CART_QUERY_KEY } from "@/lib/constants";
import { toast } from "sonner";

/**
 * Hook to sync cart with authentication state
 * - Loads anonymous cart on mount
 * - Merges anonymous cart with user cart on login
 * - Clears anonymous cart after successful merge
 */

export function useCartSync() {
  const { data: session, status } = useSession();
  const { setCart, loadFromStorage } = useCartStore();
  const queryClient = useQueryClient();

  const mergeMutation = useMutation({
    mutationFn: cartService.mergeCart,
    onSuccess: (response) => {
      if (response.error) {
        toast.error("Failed to sync cart");
        console.error("Cart merge error:", response.error);
      } else {
        // Update Zustand store with merged cart
        if (!response.data) {
          toast.error("Failed to sync cart");
          console.error("Cart merge error:", response.error);
          return;
        }
        setCart(response.data);
        // Clear anonymous cart from localStorage
        clearAnonymousCart();
        // Invalidate queries to refetch
        queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY] });
      }
    },
    onError: (error) => {
      toast.error("Failed to sync cart");
      console.error("Cart merge error:", error);
    },
  });

  // Load cart from localStorage on mount (for anonymous users)
  useEffect(() => {
    if (status === "unauthenticated") {
      loadFromStorage();
    }
  }, [status, loadFromStorage]);

  // Merge carts on login
  useEffect(() => {
    if (status === "authenticated" && session?.user?.id) {
      const anonymousCart = loadAnonymousCart();

      // Only merge if anonymous cart has items
      if (anonymousCart.items.length > 0) {
        mergeMutation.mutate({
          anonymousCart,
          userId: session.user.id,
        });
      } else {
        // No items to merge, just set userId in store
        setCart({
          ...anonymousCart,
          userId: session.user.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, session?.user?.id, mergeMutation.mutate, setCart]);

  return {
    isSyncing: mergeMutation.isPending,
  };
}
